import { ArrowRight, Play } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

export const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => setIsLoaded(true), 100);
    
    // Force video play on mobile
    const playVideo = () => {
      if (videoRef.current) {
        videoRef.current.play().catch(() => {
          // Autoplay failed, try again on user interaction
        });
      }
    };
    
    playVideo();
    document.addEventListener('touchstart', playVideo, { once: true });
    document.addEventListener('click', playVideo, { once: true });
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('touchstart', playVideo);
      document.removeEventListener('click', playVideo);
    };
  }, []);

  return (
    <section
      id="inicio"
      className="relative w-full min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Video Container - Full Screen */}
      <div className="absolute inset-0 bg-[#051914]">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          webkit-playsinline="true"
          x5-playsinline="true"
          className="w-full h-full object-cover scale-110 md:scale-125"
        >
          <source 
            src="https://customer-assets.emergentagent.com/job_prestagro-agro/artifacts/41akhptb_fields-agriculture-2025-12-17-15-33-56-utc.mp4" 
            type="video/mp4" 
          />
        </video>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#051914]/80 via-[#051914]/40 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            {/* Overline */}
            <span 
              className={`inline-block text-sm font-bold uppercase tracking-[0.2em] text-[#A9CB68] mb-6 transition-all duration-1000 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '500ms' }}
            >
              Plataforma Agropecuaria
            </span>

            {/* Main Heading */}
            <h1 
              className={`text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#FBF5EA] leading-tight mb-6 transition-all duration-1000 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: '800ms' }}
            >
              Servicios Rurales{' '}
              <span className="text-[#A9CB68]">al instante</span>
            </h1>

            {/* Subheading */}
            <p 
              className={`text-lg md:text-xl leading-relaxed text-[#FBF5EA]/80 mb-10 max-w-xl transition-all duration-1000 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: '1100ms' }}
            >
              Conectamos productores y prestadores rurales en Argentina con pagos
              flexibles y garantías. Digitaliza la contratación de servicios
              agropecuarios desde tu teléfono.
            </p>

            {/* CTA Buttons */}
            <div 
              className={`flex flex-wrap gap-4 transition-all duration-1000 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: '1400ms' }}
            >
              <a
                href="#contacto"
                className="inline-flex items-center gap-2 bg-[#A9CB68] text-[#051914] font-medium px-8 py-4 rounded-full hover:bg-[#98BA57] transition-all hover:-translate-y-1"
                data-testid="hero-cta-register"
              >
                Regístrate gratis
                <ArrowRight size={20} />
              </a>
              <a
                href="#solucion"
                className="inline-flex items-center gap-2 border-2 border-[#FBF5EA] text-[#FBF5EA] font-medium px-8 py-4 rounded-full hover:bg-[#FBF5EA] hover:text-[#051914] transition-all"
                data-testid="hero-cta-learn"
              >
                Conocé más
              </a>
            </div>

            {/* Stats */}
            <div 
              className={`flex flex-wrap gap-8 mt-16 transition-all duration-1000 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: '1700ms' }}
            >
              <div>
                <span className="block text-3xl font-semibold text-[#A9CB68]">
                  1,500+
                </span>
                <span className="text-sm text-[#FBF5EA]/60">
                  Productores activos
                </span>
              </div>
              <div>
                <span className="block text-3xl font-semibold text-[#A9CB68]">
                  800+
                </span>
                <span className="text-sm text-[#FBF5EA]/60">
                  Prestadores verificados
                </span>
              </div>
              <div>
                <span className="block text-3xl font-semibold text-[#A9CB68]">
                  5,000+
                </span>
                <span className="text-sm text-[#FBF5EA]/60">
                  Servicios realizados
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 rounded-full border-2 border-[#FBF5EA]/30 flex items-start justify-center pt-2">
          <div className="w-1.5 h-3 bg-[#FBF5EA]/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};
